import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '300px'};
  position: relative;

  svg {
    width: 100%;
    height: 100%;
  }

  .tooltip {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1000;
  }

  .timeline-event {
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      opacity: 0.8;
    }
  }

  .axis text {
    font-family: 'Segoe UI', sans-serif;
    font-size: 11px;
  }

  .axis-label {
    font-family: 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 600;
  }

  .event-label {
    font-family: 'Segoe UI', sans-serif;
    font-size: 11px;
    pointer-events: none;
  }
`;

/**
 * D3 Timeline Component
 * @param {Array} data - Array of objects with format: [{date: Date|string, label: string, value: number, ...}]
 * @param {String} dateKey - Key for date values
 * @param {String} labelKey - Key for event labels
 * @param {String} valueKey - Key for event values (optional, affects circle size)
 * @param {Function} onClick - Click handler receiving the clicked event
 * @param {String} color - Event color (default: LinkedIn Blue)
 * @param {String} height - Chart height (default: 300px)
 * @param {Boolean} showLabels - Show event labels (default: true)
 */
const D3Timeline = ({ 
  data = [], 
  dateKey = 'date', 
  labelKey = 'label',
  valueKey = null,
  onClick = null,
  color = '#0077B5',
  height = '300px',
  showLabels = true
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 300 });

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height: parseInt(height) });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      handleResize();
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [height]);

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Increase margins to accommodate staggered labels
    const margin = { top: 80, right: 30, bottom: 80, left: 60 };
    const width = dimensions.width - margin.left - margin.right;
    const chartHeight = dimensions.height - margin.top - margin.bottom;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Parse dates
    const parseDate = d => {
      const date = d[dateKey];
      return date instanceof Date ? date : new Date(date);
    };

    // X scale (time)
    const x = d3.scaleTime()
      .domain(d3.extent(data, parseDate))
      .range([0, width]);

    // Radius scale (if valueKey provided)
    const radiusScale = valueKey
      ? d3.scaleSqrt()
          .domain([0, d3.max(data, d => d[valueKey])])
          .range([4, 12])
      : null;

    // Draw timeline axis line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', chartHeight / 2)
      .attr('y2', chartHeight / 2)
      .attr('stroke', '#ccc')
      .attr('stroke-width', 2);

    // X Axis
    g.append('g')
      .attr('class', 'axis')
      .attr('transform', `translate(0,${chartHeight / 2 + 20})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat(d3.timeFormat('%b %Y')))
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');

    // Create events
    const events = g.selectAll('.timeline-event')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'timeline-event')
      .attr('transform', d => `translate(${x(parseDate(d))},${chartHeight / 2})`);

    // Vertical lines from timeline to events
    events.append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', (d, i) => i % 2 === 0 ? -30 : 30)
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .style('opacity', 0)
      .transition()
      .duration(800)
      .delay((d, i) => i * 100)
      .style('opacity', 1);

    // Event circles
    events.append('circle')
      .attr('cy', (d, i) => i % 2 === 0 ? -30 : 30)
      .attr('r', 0)
      .attr('fill', color)
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .on('click', (event, d) => {
        if (onClick) onClick(d);
      })
      .on('mouseover', (event, d) => {
        if (tooltipRef.current) {
          const dateStr = d3.timeFormat('%B %d, %Y')(parseDate(d));
          let html = `<strong>${d[labelKey]}</strong><br/>Date: ${dateStr}`;
          if (valueKey) {
            html += `<br/>Value: ${d[valueKey].toLocaleString()}`;
          }
          d3.select(tooltipRef.current)
            .style('opacity', 1)
            .html(html)
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 20}px`);
        }
      })
      .on('mouseout', () => {
        if (tooltipRef.current) {
          d3.select(tooltipRef.current).style('opacity', 0);
        }
      })
      .transition()
      .duration(800)
      .delay((d, i) => i * 100)
      .attr('r', d => radiusScale ? radiusScale(d[valueKey]) : 6);

    // Event labels with continuous stacking (no reset)
    if (showLabels) {
      // Calculate label positions with stacking
      const labelSpacing = 18; // Vertical spacing between stacked labels
      const proximityThreshold = 2; // Pixels - labels within this distance are considered at same date
      const labelOffsets = new Array(data.length).fill(0);
      
      // Store x-positions and sides for each label
      const labelData = data.map((d, i) => ({
        xPos: x(parseDate(d)),
        isAbove: i % 2 === 0,
        side: (i % 2 === 0) ? 'above' : 'below',
        index: i
      }));
      
      // Group labels by x-position (same date or very close dates)
      // Map: rounded x-position -> { above: [], below: [] }
      const positionGroups = new Map();
      
      labelData.forEach((label) => {
        // Round x-position to group nearby labels together
        const roundedX = Math.round(label.xPos / proximityThreshold) * proximityThreshold;
        
        if (!positionGroups.has(roundedX)) {
          positionGroups.set(roundedX, { above: [], below: [] });
        }
        
        const group = positionGroups.get(roundedX);
        if (label.side === 'above') {
          group.above.push(label);
        } else {
          group.below.push(label);
        }
      });
      
      // For each position group, stack labels continuously without resetting
      positionGroups.forEach((group) => {
        // Stack above labels continuously
        group.above.forEach((label, i) => {
          labelOffsets[label.index] = labelSpacing * i;
        });
        
        // Stack below labels continuously
        group.below.forEach((label, i) => {
          labelOffsets[label.index] = labelSpacing * i;
        });
      });
      
      events.append('text')
        .attr('class', 'event-label')
        .attr('y', (d, i) => {
          const baseOffset = i % 2 === 0 ? -40 : 40;
          const collisionOffset = i % 2 === 0 ? -labelOffsets[i] : labelOffsets[i];
          return baseOffset + collisionOffset;
        })
        .attr('dy', (d, i) => i % 2 === 0 ? '-0.5em' : '1em')
        .attr('text-anchor', 'middle')
        .attr('dx', 0)
        .style('font-size', '11px')
        .style('font-weight', '600')
        .style('fill', '#2C3E50')
        .style('opacity', 0)
        .text(d => {
          const label = d[labelKey];
          // Truncate very long labels to prevent overlap
          const maxLength = 20;
          return label.length > maxLength ? label.substring(0, maxLength) + '...' : label;
        })
        .transition()
        .duration(800)
        .delay((d, i) => i * 100)
        .style('opacity', 1);
      
      // Add connecting lines from events to labels for clarity
      events.append('line')
        .attr('class', 'label-connector')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', (d, i) => i % 2 === 0 ? -30 : 30)
        .attr('y2', (d, i) => {
          const baseOffset = i % 2 === 0 ? -40 : 40;
          const collisionOffset = i % 2 === 0 ? -labelOffsets[i] : labelOffsets[i];
          return baseOffset + collisionOffset;
        })
        .attr('stroke', '#ccc')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2,2')
        .style('opacity', 0)
        .transition()
        .duration(800)
        .delay((d, i) => i * 100 + 400)
        .style('opacity', 0.5);
    }

  }, [data, dimensions, dateKey, labelKey, valueKey, onClick, color, showLabels]);

  return (
    <ChartContainer ref={containerRef} height={height}>
      <svg ref={svgRef}></svg>
      <div ref={tooltipRef} className="tooltip"></div>
    </ChartContainer>
  );
};

export default D3Timeline;


